"use client";

import { Check } from "@/components/icons/check";
import { Spinner } from "@/components/icons/spinner";
import {
	type ComponentType,
	type ElementType,
	type JSX,
	type JSXElementConstructor,
	type PropsWithChildren,
	type ReactNode,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { cnBase } from "tailwind-variants";

const StatusContext = createContext<{
	success: boolean;
	error: boolean;
	pending: boolean;
	setPending?: (pending: boolean) => void;
	setSuccess?: (success: boolean) => void;
	setError?: (error: boolean) => void;
}>({ success: false, error: false, pending: false });

export function useHandlersWithStatus<
	T extends Record<
		string,
		((...params: any[]) => any | Promise<any>) | undefined
	>,
>(handlers: T, debounce?: boolean): T {
	const context = useContext(StatusContext);
	const timeout = useRef<ReturnType<typeof setTimeout>>(null);
	return useMemo(
		() =>
			Object.entries(handlers).reduce((result, [name, callback]) => {
				if (callback) {
					result[name] = (...params: any[]) => {
						if (debounce) {
							if (timeout.current) {
								clearTimeout(timeout.current);
							}
							timeout.current = setTimeout(() => {
								const value = callback(...params);
								if (value instanceof Promise) {
									context.setPending?.(true);
									value
										.then(
											() => context.setSuccess?.(true),
											() => context.setError?.(true),
										)
										.finally(() => context.setPending?.(false));
								}
							}, 500);
						} else {
							const value = callback(...params);
							if (value instanceof Promise) {
								context.setPending?.(true);
								value
									.then(
										() => context.setSuccess?.(true),
										() => context.setError?.(true),
									)
									.finally(() => context.setPending?.(false));
							}
						}
					};
				}
				return result;
			}, {} as any),
		[handlers],
	);
}

export function StatusProvider({
	children,
	timeout = 2000,
	errorTimeout = 4000,
	initialSuccess = false,
}: PropsWithChildren<{
	group?: string;
	timeout?: number | false;
	errorTimeout?: number | false;
	initialSuccess?: boolean;
}>) {
	const [success, setSuccess] = useState<boolean>(initialSuccess);
	const [error, setError] = useState<boolean>(false);
	const [pending, setPending] = useState<boolean>(false);
	const isMounted = useRef(true);

	useEffect(() => {
		isMounted.current = true;
		return () => {
			isMounted.current = false;
		};
	}, []);

	const setSuccessCallback = useCallback(
		(value: boolean) => {
			setSuccess(value);
			if (value && timeout !== false) {
				setTimeout(() => isMounted.current && setSuccess(false), timeout);
			}
		},
		[setSuccess],
	);

	const setPendingCallback = useCallback(
		(value: boolean) => {
			setPending(value);
		},
		[setPending],
	);

	const setErrorCallback = useCallback(
		(value: boolean) => {
			setError(value);
			if (value && errorTimeout !== false) {
				setTimeout(() => isMounted.current && setError(false), errorTimeout);
			}
		},
		[setError],
	);

	return (
		<StatusContext.Provider
			value={{
				setSuccess: setSuccessCallback,
				setError: setErrorCallback,
				setPending: setPendingCallback,
				success,
				error,
				pending,
			}}
		>
			{children}
		</StatusContext.Provider>
	);
}

type Props<T extends ElementType> = (T extends JSXElementConstructor<infer P>
	? P
	: T extends keyof JSX.IntrinsicElements
		? JSX.IntrinsicElements[T]
		: never) & {
	error?: ReactNode;
	pending?: ReactNode;
	success?: ReactNode;
	as?: ComponentType<T>;
	children?: ReactNode;
};

export function WithStatus<T extends ElementType>(props: Props<T>) {
	const context = useContext(StatusContext);
	let children = props.children;
	if (context.error && props.error) {
		children = props.error;
	} else if (context.pending && props.pending) {
		children = props.pending;
	} else if (context.success && props.success) {
		children = props.success;
	}
	if (props.as) {
		const Component = props.as;
		return (
			<Component
				data-success={context.success || undefined}
				data-pending={context.pending || undefined}
				data-error={context.error || undefined}
				{...props}
			>
				{children ?? null}
			</Component>
		);
	}
	return children ?? null;
}

export function Status({
	success,
	children,
	className,
}: {
	success?: ReactNode;
	children?: ReactNode;
	className?: string;
}) {
	return (
		<WithStatus
			as="div"
			className={cnBase("group/status inline-flex items-center", className)}
		>
			<div
				aria-hidden
				className="grid grid-cols-[0px] group-data-success/status:grid-cols-[16px] transition-[grid-template-columns] overflow-hidden"
			>
				<Check className="size-5 -ml-1" />
			</div>
			<div
				aria-hidden
				className="grid grid-cols-[0px] group-data-pending/status:grid-cols-[16px] transition-[grid-template-columns] overflow-hidden"
			>
				<Spinner className="size-5 -ml-1" />
			</div>
			{children ? (
				<div
					className={
						success
							? "grid grid-cols-[auto] group-data-success/status:grid-cols-[0px] transition-[grid-template-columns] overflow-hidden"
							: undefined
					}
				>
					{children}
				</div>
			) : null}
			{success ? (
				<div className="grid grid-cols-[0px] group-data-success/status:grid-cols-[auto] transition-[grid-template-columns] overflow-hidden">
					{success}
				</div>
			) : null}
		</WithStatus>
	);
}
